import {
  Component,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SavedPlatesService } from 'src/app/services/saved-plates.service';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AUCTIONS_DEV_ONLY, AuctionLotsService } from 'src/app/services/auction-lots-service';
import { SessionService } from 'src/app/services/session-service';
import { marketGuidesEnabled, site } from 'src/app/site-config';
import { platePath } from 'src/app/utils/canonical-url';
import { seriesPath } from 'src/app/utils/series-url';
import { matchRegistration } from 'src/app/utils/registration-match';

interface NavLink {
  label: string;
  route?: string; // omitted => placeholder / not yet wired (e.g. Press)
  soon?: boolean; // render a small "soon" badge, non-navigating
  queryParams?: { [k: string]: string }; // e.g. /auctions?tab=today
  live?: boolean; // show the live-lot count pill (Live auctions)
}

interface MegaSection {
  title: string;
  links: NavLink[];
}

// Launch navigation (staging / www): Search | New Releases | PlateViewer, with
// saved plates as the heart beside the search and the account behind Sign in /
// the person icon (owner, 2026-11-03). Search and New Releases are compact dropdowns that
// keep the specialist pages one click away; only finished pages are linked.
const LAUNCH_SEARCH: NavLink[] = [
  { label: 'Super Search', route: '/' },
  { label: 'Initials', route: '/plates/initials' },
  { label: 'Dateless', route: '/plates/dateless' },
  { label: 'Name plates', route: '/plates/name-plates' },
  { label: "Number 1's", route: '/plates/number-1' },
  { label: 'Under £200', route: '/plates/budget/under-200' },
  // Build Your Own returns once its redesigned page ships.
];

const LAUNCH_RELEASES: NavLink[] = [
  { label: '27 Reg (coming)', route: seriesPath('27') },
  { label: '76 Reg', route: seriesPath('76') },
  { label: '26 Reg', route: seriesPath('26') },
  { label: '75 Reg', route: seriesPath('75') },
  { label: '25 Reg', route: seriesPath('25') },
  { label: 'All registration series', route: '/plates/current-style' },
];

// Insights & Opinion on release hosts, only where Market Guides is switched on
// (staging review); www keeps the approved launch navigation.
const LAUNCH_INSIGHTS: NavLink[] = [
  { label: 'Market Guides', route: '/market-guides' },
  { label: 'Number Plate News', route: '/blog' },
];

/**
 * Site-wide navigation shell (dev2 rollout): a thin announcement strip, a white
 * top header (logo + top-level nav + compact search + account icons), a
 * full-width Search mega-menu (desktop) and a slide-in mobile drawer. Built as
 * ONE shared component so it can become the canonical header across every page
 * (homepage, search results, plate-detail-v2, future Sell/Insights/Dealers/Press).
 *
 * First pass moves existing destinations as-is and preserves existing routes;
 * no naming optimisation. Gated to dev2 in app.component (legacy header on prod).
 */
@Component({
  selector: 'pla-nav-shell',
  templateUrl: './nav-shell.component.html',
  styleUrls: ['./nav-shell.component.scss'],
})
export class NavShellComponent implements OnInit, OnDestroy {
  // Editable later (could move to a service / CMS). Single source of truth here.
  // Release hosts show no strip (the lottery promotion is not part of the launch).
  public readonly launch = site.launchNav;
  public announcement = this.launch
    ? ''
    : 'Track your plate to enter our weekly plate lottery draw';
  public readonly launchMenus: { id: string; label: string; links: NavLink[] }[] = [
    { id: 'search', label: 'Search', links: LAUNCH_SEARCH },
    { id: 'releases', label: 'New Releases', links: LAUNCH_RELEASES },
    ...(marketGuidesEnabled ? [{ id: 'insights', label: 'Insights & Opinion', links: LAUNCH_INSIGHTS }] : []),
  ];
  public openDrop: string | null = null;

  public megaOpen = false;
  public mobileOpen = false;
  public mobileSearchExpanded = true;
  public searchInput = '';
  public searchFocused = false;
  // Mobile drop-down search bar under the header (magnifier toggle).
  public searchBarOpen = false;

  // On Search Results the header search becomes the PRIMARY search entry — it
  // widens and gains visual weight (see .ns--search-page in the stylesheet), and
  // is prefilled with the active term so refining a search is one edit away.
  // Toggled from the router URL below.
  @HostBinding('class.ns--search-page') public isSearchPage = false;

  // Top-level nav (right of the Search ▾ trigger). Press has no route yet — it
  // renders as a present-but-inert placeholder until the Press page exists.
  // "Live auctions" is DEV-only (dev2/local, same gate as app.module
  // isDevOnly) — production nav is unchanged.
  public topNav: NavLink[] = this.launch ? [{ label: 'PlateViewer', route: '/plate-viewer' }] : [
    ...(AUCTIONS_DEV_ONLY ? [{ label: 'Live auctions', route: '/auctions', live: true }] : []),
    { label: 'Sell Your Plate', route: '/sell' },
    { label: 'Insights & Opinion', route: '/blog' },
    { label: 'Dealers', route: '/dealer/home' },
    { label: 'Press', soon: true },
  ];

  // Search mega-menu. This shell renders on dev2 only (gated in
  // app.component), so the register links below go DIRECT to the redesigned
  // register pages as they are reviewable on dev2 today:
  //   Initials  -> /plates/initials  (the Initials Reference on every host)
  //   Dateless  -> /plates/dateless  (isDev2 serves the Dateless Reference)
  //   Names     -> /names            (serves the Perfect Register on dev2)
  //   Number 1s -> /plates/number-1  (isDev2 serves the Number Ones Register)
  public megaSections: MegaSection[] = this.launch
    ? [
        { title: 'Search', links: LAUNCH_SEARCH },
        { title: 'New Releases', links: LAUNCH_RELEASES },
        ...(marketGuidesEnabled ? [{ title: 'Insights & Opinion', links: LAUNCH_INSIGHTS }] : []),
      ]
    : [
    {
      title: 'Search tools',
      links: [
        { label: 'Super Search', route: '/buy' },
        { label: 'Initials', route: '/plates/initials' },
        { label: 'Dateless', route: '/plates/dateless' },
        { label: 'Build Your Own', route: '/byo-search' },
      ],
    },
    {
      title: 'Collections',
      links: [
        { label: 'Names', route: '/plates/name-plates' },
        { label: "Number 1's", route: '/plates/number-1' },
        { label: 'Under £200', route: '/under-200' },
        { label: 'New Releases', route: '/plates/26-reg' },
      ],
    },
    // DEV-only Auctions column (dev2/local); absent on production.
    ...(AUCTIONS_DEV_ONLY
      ? [
          {
            title: 'Auctions',
            links: [
              { label: 'Live auctions', route: '/auctions' },
              { label: 'Closing today', route: '/auctions', queryParams: { tab: 'today' } },
              { label: 'Coming soon', route: '/auctions', queryParams: { tab: 'soon' } },
              { label: 'Results', route: '/auctions', queryParams: { tab: 'results' } },
            ],
          },
        ]
      : []),
    {
      title: 'Registration years',
      links: [
        { label: '26 Reg', route: '/plates/26-reg' },
        { label: '76 Reg', route: '/plates/76-reg' },
        // Pre-release until DVLA's November sale — page shows TBC prices.
        { label: '27 Reg (coming)', route: '/plates/27-reg' },
      ],
    },
    {
      title: 'Tools',
      links: [
        { label: 'Plate Viewer', route: '/plate-viewer' },
        { label: 'Number Plate Guide', route: '/guides/number-plate-types' },
      ],
    },
    {
      title: 'Information',
      links: [
        { label: 'About PlateX', route: '/about' },
        { label: 'FAQs', route: '/info' },
      ],
    },
  ];

  private routerSub?: Subscription;
  private liveSub?: Subscription;

  /** Live-lot count for the "Live auctions" pill (null = hidden). */
  public liveCount: number | null = null;

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private auctionLots: AuctionLotsService,
    private savedPlates: SavedPlatesService
  ) {}

  public get savedCount(): number {
    return this.savedPlates.count;
  }

  toggleDrop(id: string): void {
    this.openDrop = this.openDrop === id ? null : id;
    this.accountOpen = false;
  }

  // Header heart → saved plates, no sign-in box: signed out it opens the
  // device shortlist (/saved), signed in the account's Saved plates.
  openSaved(): void {
    this.closeAll();
    this.router.navigate([this.sessionService.isLoggedIn() ? '/account/saved' : '/saved']);
  }

  // The desktop account icons are hidden at the mobile breakpoint, so the
  // drawer is the ONLY route to /account (and /account/admin) on mobile.
  public get isAdmin(): boolean {
    return this.sessionService.isAdmin();
  }

  public get isLoggedIn(): boolean {
    return this.sessionService.isLoggedIn();
  }

  // ---- account menu (desktop person icon) ----
  public accountOpen = false;

  toggleAccount(): void {
    this.accountOpen = !this.accountOpen;
  }

  // Click anywhere outside the account menu closes it.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (this.openDrop && !target?.closest('.ns-drop')) this.openDrop = null;
    if (!this.accountOpen) return;
    if (!target?.closest('.ns-account')) this.accountOpen = false;
  }

  signOut(): void {
    this.closeAll();
    this.sessionService.signOut(() => {
      this.sessionService.sessionStateChange.emit();
      this.router.navigate(['/']);
    });
  }

  ngOnInit(): void {
    // Close any open menu/drawer on route change so navigation never leaves the
    // overlay or drawer hanging. On NavigationEnd, recompute the search-page
    // state so the header search elevates only on the results page.
    this.routerSub = this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.closeAll();
      }
      if (e instanceof NavigationEnd) {
        this.syncSearchPage(e.urlAfterRedirects || e.url);
      }
    });
    // Initial load (router events may have already fired before subscription).
    this.syncSearchPage(this.router.url);

    if (AUCTIONS_DEV_ONLY) {
      this.liveSub = this.auctionLots.liveCount$.subscribe((n) => (this.liveCount = n));
      this.auctionLots.ensureLiveCount();
    }
  }

  // True on the results page — where the header search is the primary entry.
  // Also prefills the box with the active term so it reads as an editable search.
  private syncSearchPage(url: string): void {
    const path = (url || '').split('?')[0];
    this.isSearchPage = /^\/search-results(\/|$)|^\/search-prod-style(\/|$)/.test(path);
    if (this.isSearchPage) {
      const seg = path.replace(/^\/search-(results|prod-style)\/?/, '');
      const term = decodeURIComponent(seg || '').trim();
      if (term) this.searchInput = term.toUpperCase();
    }
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.liveSub?.unsubscribe();
    this.unlockBody();
  }

  // ---- desktop mega-menu ----
  toggleMega(): void {
    this.megaOpen ? this.closeMega() : this.openMega();
  }
  openMega(): void {
    this.megaOpen = true;
  }
  closeMega(): void {
    this.megaOpen = false;
  }

  // ---- mobile drawer ----
  openMobile(): void {
    this.mobileOpen = true;
    this.lockBody();
  }
  closeMobile(): void {
    this.mobileOpen = false;
    this.unlockBody();
  }
  toggleMobileSearch(): void {
    this.mobileSearchExpanded = !this.mobileSearchExpanded;
  }

  // ---- mobile drop-down search bar ----
  // Focus happens inside the tap (not after a timeout) so iOS raises the keyboard.
  toggleSearchBar(input?: HTMLInputElement): void {
    this.searchBarOpen = !this.searchBarOpen;
    if (this.searchBarOpen) input?.focus();
    else input?.blur();
  }

  closeAll(): void {
    this.megaOpen = false;
    this.searchBarOpen = false;
    this.accountOpen = false;
    this.openDrop = null;
    if (this.mobileOpen) {
      this.mobileOpen = false;
      this.unlockBody();
    }
  }

  // Escape closes whatever is open (desktop mega or mobile drawer).
  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeAll();
  }

  // The term as one registration ("XJ 220"), so the box can offer that
  // plate's page next to the normal broad search (Enter still searches).
  get plateMatch(): string | null {
    return matchRegistration(this.searchInput);
  }

  goToPlate(): void {
    const reg = this.plateMatch;
    if (!reg) return;
    this.closeAll();
    this.searchFocused = false;
    this.router.navigateByUrl(platePath(reg));
    this.searchInput = '';
  }

  // Reuse the EXISTING search route (no ranking / service change).
  runSearch(): void {
    const q = (this.searchInput || '').trim();
    if (!q) return;
    this.closeAll();
    this.router.navigate([`/search-results/${q}`]);
    this.searchInput = '';
  }

  // Body scroll lock while the mobile drawer is open (no hover dependency).
  private lockBody(): void {
    if (typeof document !== 'undefined') document.body.style.overflow = 'hidden';
  }
  private unlockBody(): void {
    if (typeof document !== 'undefined') document.body.style.overflow = '';
  }
}
